import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { formatVendorDeviceCellLabel } from '../../../components/vendorAndDeviceRadioList';
import { getComputeResourceConfigurations } from '../api';
import { isGpuInventoryResourceType } from '../domain/gpuInventory';
import type { AllowedGpuFilter, ComputeInventoryResourceType, ComputeWorkloadType } from './types';

export type { AllowedGpuFilter, ComputeInventoryResourceType, ComputeWorkloadType } from './types';

export interface UseComputeVendorDeviceInventoryOptions {
  resourceType: ComputeInventoryResourceType;
  workloadType?: ComputeWorkloadType;
  architecture?: string;
  allowedGpus?: AllowedGpuFilter[];
  hide?: boolean;
}

interface InventoryItem {
  resourceType?: string;
  vendor?: string;
  device?: string;
  vendorID?: string;
  deviceID?: string;
  deviceType?: string;
  type?: string;
  architecture?: string;
  quantity?: { formation?: number[]; min?: number; max?: number; resourceName?: string };
  gpu?: { videoRAM?: { capacity?: string | number }; software?: { driverVersion?: string; cudaVersion?: string }[] };
}

function buildAllowedGpuKeySet(allowedGpus?: AllowedGpuFilter[]) {
  if (!allowedGpus?.length) return null;
  const result = new Set<string>();
  allowedGpus.forEach((item) => {
    const resourceType = String(item?.resourceType ?? '').trim();
    const vendorID = String(item?.vendorID ?? '')
      .trim()
      .toUpperCase();
    const deviceID = String(item?.deviceID ?? '')
      .trim()
      .toUpperCase();
    if (vendorID && deviceID) {
      result.add(resourceType ? `${resourceType}:${vendorID}:${deviceID}` : `${vendorID}:${deviceID}`);
    }
  });
  return result.size > 0 ? result : null;
}

function matchesAllowedGpu(item: InventoryItem, allowedGpuKeySet: Set<string> | null) {
  if (!allowedGpuKeySet) return true;
  const resourceType = String(item?.resourceType ?? '').trim();
  const vendorID = String(item?.vendorID ?? '')
    .trim()
    .toUpperCase();
  const deviceID = String(item?.deviceID ?? '')
    .trim()
    .toUpperCase();
  if (!vendorID || !deviceID) return false;
  if (resourceType && allowedGpuKeySet.has(`${resourceType}:${vendorID}:${deviceID}`)) return true;
  return allowedGpuKeySet.has(`${vendorID}:${deviceID}`);
}

/** 拉取 CPU / 内存 / GPU 厂商设备清单（不计价场景，与 GPU 共用接口，仅 resourceType 不同） */
export function useComputeVendorDeviceInventory({
  resourceType,
  workloadType = 'VirtualMachine',
  architecture,
  allowedGpus,
  hide,
}: UseComputeVendorDeviceInventoryOptions) {
  const [data, setData] = useState<InventoryItem[]>([]);
  const originalData = useRef<InventoryItem[]>([]);
  const allowedGpuKeySet = useMemo(() => buildAllowedGpuKeySet(allowedGpus), [allowedGpus]);

  const filterData = useCallback(
    (list: InventoryItem[]) => {
      let next = Array.isArray(list) ? list : [];
      if (architecture) {
        next = next.filter((item) => !item.architecture || item.architecture === architecture);
      }
      if (allowedGpuKeySet) {
        next = next.filter((item) => matchesAllowedGpu(item, allowedGpuKeySet));
      }
      return next;
    },
    [allowedGpuKeySet, architecture],
  );

  const requestArchitecture = architecture && isGpuInventoryResourceType(resourceType) ? architecture : undefined;

  useEffect(() => {
    if (hide) return;
    getComputeResourceConfigurations({
      workloadType,
      resourceType,
      ...(requestArchitecture ? { architecture: requestArchitecture } : {}),
    })
      .then((res) => {
        if (Array.isArray(res)) {
          originalData.current = res as InventoryItem[];
          setData(filterData(res as InventoryItem[]));
        } else {
          originalData.current = [];
          setData([]);
        }
      })
      .catch(console.error);
  }, [hide, workloadType, resourceType, requestArchitecture, filterData]);

  useEffect(() => {
    setData(filterData(originalData.current));
  }, [filterData]);

  const resourceTypeOptions = useMemo(
    () =>
      [...new Set(data.map((item) => item.resourceType).filter(Boolean))].map((value) => ({
        label: value,
        value,
      })),
    [data],
  );

  const filterByResourceType = useCallback(
    (scopedResourceType?: string, list: InventoryItem[] = data) => {
      if (!scopedResourceType) return list;
      return list.filter((item) => item.resourceType === scopedResourceType);
    },
    [data],
  );

  const getVendorOptions = useCallback(
    (scopedResourceType?: string) => {
      const scoped = filterByResourceType(scopedResourceType);
      return [...new Set(scoped.map((item) => item.vendor).filter(Boolean))].map((vendor) => ({
        label: formatVendorDeviceCellLabel(vendor),
        value: vendor,
      }));
    },
    [filterByResourceType],
  );

  const getDeviceOptions = useCallback(
    (scopedResourceType: string | undefined, vendor: string) => {
      const scoped = filterByResourceType(scopedResourceType);
      const devices = [
        ...new Set(
          scoped
            .filter((item) => item.vendor === vendor)
            .map((item) => item.device)
            .filter(Boolean),
        ),
      ];
      return devices.map((device) => ({ label: formatVendorDeviceCellLabel(device), value: device }));
    },
    [filterByResourceType],
  );

  const getHardwareItem = useCallback(
    (scopedResourceType: string | undefined, vendor: string, device: string) => {
      const scoped = filterByResourceType(scopedResourceType);
      return scoped.find(
        (item) => item.vendor === vendor && item.device === device && (!scopedResourceType || item.resourceType === scopedResourceType),
      );
    },
    [filterByResourceType],
  );

  const getNumberOptions = useCallback(
    (scopedResourceType: string | undefined, vendor: string, device: string) => {
      const item = getHardwareItem(scopedResourceType, vendor, device);
      if (!item) return [];
      const { formation = [], min = 0, max = Infinity } = item.quantity || {};
      return formation.filter((num) => num >= min && num <= max).map((num) => ({ label: String(num), value: num }));
    },
    [getHardwareItem],
  );

  const getDriverOptions = useCallback(
    (scopedResourceType: string | undefined, vendor: string, device: string) => {
      const curr = getHardwareItem(scopedResourceType, vendor, device);
      const versions = [...new Set((curr?.gpu?.software || []).map((item) => item?.driverVersion).filter(Boolean))];
      return versions.map((version) => ({ label: String(version), value: String(version) }));
    },
    [getHardwareItem],
  );

  const getCudaOptions = useCallback(
    (scopedResourceType: string | undefined, vendor: string, device: string, driverVersion?: string) => {
      const curr = getHardwareItem(scopedResourceType, vendor, device);
      const softwareList = curr?.gpu?.software || [];
      const filtered = driverVersion ? softwareList.filter((item) => item?.driverVersion === driverVersion) : softwareList;
      const versions = [...new Set(filtered.map((item) => item?.cudaVersion).filter(Boolean))];
      return versions.map((version) => ({ label: String(version), value: String(version) }));
    },
    [getHardwareItem],
  );

  return {
    data,
    resourceTypeOptions,
    getVendorOptions,
    getDeviceOptions,
    getNumberOptions,
    getHardwareItem,
    getDriverOptions,
    getCudaOptions,
  };
}
